import type { UserContext } from './types';

export const ACTIVE_AGENCY_COOKIE = 'neutralaiz-active-agency-id';
export const ACTIVE_DEPARTMENT_COOKIE = 'neutralaiz-active-department-id';

const ACTIVE_SCOPE_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

export interface ActiveOrgScope {
  activeAgencyId: string | null;
  activeDepartmentId: string | null;
}

type ActiveScopeUserContext = Pick<
  UserContext,
  'agencies' | 'departments' | 'active_agency_id' | 'active_department_id'
>;

type CookieStoreLike = {
  get: (name: string) => { value?: string } | undefined;
};

function normalizeScopeId(value?: string | null): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

export function resolveActiveOrgScope(
  userContext: ActiveScopeUserContext | null,
  preferred?: Partial<ActiveOrgScope>
): ActiveOrgScope {
  if (!userContext) {
    return {
      activeAgencyId: null,
      activeDepartmentId: null,
    };
  }

  const validAgencyIds = new Set<string>([
    ...userContext.agencies.map((agency) => agency.agency_id),
    ...userContext.departments.map((department) => department.agency_id).filter(Boolean),
  ]);
  const validDepartmentIds = new Set(
    userContext.departments.map((department) => department.department_id)
  );

  let activeDepartmentId =
    preferred?.activeDepartmentId && validDepartmentIds.has(preferred.activeDepartmentId)
      ? preferred.activeDepartmentId
      : userContext.active_department_id && validDepartmentIds.has(userContext.active_department_id)
        ? userContext.active_department_id
        : userContext.departments.length === 1
          ? userContext.departments[0].department_id
          : null;

  let activeAgencyId =
    preferred?.activeAgencyId && validAgencyIds.has(preferred.activeAgencyId)
      ? preferred.activeAgencyId
      : userContext.active_agency_id && validAgencyIds.has(userContext.active_agency_id)
        ? userContext.active_agency_id
        : null;

  if (activeDepartmentId) {
    const department = userContext.departments.find(
      (entry) => entry.department_id === activeDepartmentId
    );
    activeAgencyId = department?.agency_id || activeAgencyId;
  } else if (!activeAgencyId && userContext.agencies.length === 1) {
    activeAgencyId = userContext.agencies[0].agency_id;
  } else if (!activeAgencyId && userContext.departments.length === 1) {
    activeAgencyId = userContext.departments[0].agency_id || null;
  }

  return {
    activeAgencyId,
    activeDepartmentId,
  };
}

export function readActiveOrgScopeFromCookieStore(
  cookieStore: CookieStoreLike
): ActiveOrgScope {
  return {
    activeAgencyId: normalizeScopeId(cookieStore.get(ACTIVE_AGENCY_COOKIE)?.value),
    activeDepartmentId: normalizeScopeId(cookieStore.get(ACTIVE_DEPARTMENT_COOKIE)?.value),
  };
}

function getCookieAttributes(maxAgeSeconds: number): string {
  const attrs = ['path=/', `Max-Age=${maxAgeSeconds}`, 'SameSite=Lax'];

  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    attrs.push('Secure');
  }

  return attrs.join('; ');
}

function setCookie(name: string, value: string): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; ${getCookieAttributes(ACTIVE_SCOPE_COOKIE_MAX_AGE_SECONDS)}`;
}

function clearCookie(name: string): void {
  document.cookie = `${name}=; ${getCookieAttributes(0)}`;
}

export function persistActiveOrgScopeCookies(scope: ActiveOrgScope): void {
  if (typeof document === 'undefined') return;

  if (scope.activeAgencyId) {
    setCookie(ACTIVE_AGENCY_COOKIE, scope.activeAgencyId);
  } else {
    clearCookie(ACTIVE_AGENCY_COOKIE);
  }

  if (scope.activeDepartmentId) {
    setCookie(ACTIVE_DEPARTMENT_COOKIE, scope.activeDepartmentId);
  } else {
    clearCookie(ACTIVE_DEPARTMENT_COOKIE);
  }
}
